'use client';

import { type ChangeEvent, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useForm, type FieldValues } from 'react-hook-form';
import {
  Badge,
  Button,
  Form,
  FormField as DsFormField,
  Input,
  Typography,
  useToast,
} from '@org/design-system';
import type { ShadcnFormConfig, ShadcnFormField } from './types';
import { useLiveblocksLite } from './liveblocks-lite';

interface FormRendererProps {
  config: ShadcnFormConfig;
  onSuccess?: (values: Record<string, unknown>) => void;
}

interface PrimitiveFieldProps {
  children: ReactNode;
}

function FormItem({ children }: PrimitiveFieldProps) {
  return <div className="space-y-2">{children}</div>;
}

function FormLabel({ children }: PrimitiveFieldProps) {
  return <>{children}</>;
}

function FormControl({ children }: PrimitiveFieldProps) {
  return <>{children}</>;
}

const renderFieldControl = (
  field: ShadcnFormField,
  value: unknown,
  onChange: (nextValue: unknown) => void,
  disabled?: boolean,
  onFocus?: () => void,
  onBlur?: () => void,
): ReactNode => {
  switch (field.type) {
    case 'checkbox': {
      return (
        <input
          id={field.name}
          type="checkbox"
          checked={Boolean(value)}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
          className="h-4 w-4 rounded border-border"
          disabled={disabled}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      );
    }
    case 'number': {
      return (
        <Input
          id={field.name}
          type="number"
          placeholder={field.placeholder}
          value={typeof value === 'number' ? value : ''}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const parsed = Number(event.target.value);
            onChange(Number.isFinite(parsed) ? parsed : undefined);
          }}
          required={field.required}
          disabled={disabled}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      );
    }
    case 'select': {
      return (
        <select
          id={field.name}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={typeof value === 'string' ? value : ''}
          onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.value)}
          required={field.required}
        >
          <option value="">Select an option</option>
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      );
    }
    case 'email':
    case 'text':
    default:
      return (
        <Input
          id={field.name}
          type={field.type === 'email' ? 'email' : 'text'}
          placeholder={field.placeholder}
          value={typeof value === 'string' ? value : ''}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
          required={field.required}
          disabled={disabled}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      );
  }
};

export function FormRenderer({ config, onSuccess }: FormRendererProps) {
  const form = useForm<FieldValues>({
    defaultValues: config.defaultValues,
  });
  const { toast } = useToast();

  const formId = config.id ?? config.title ?? 'default-form';
  const roomId = config.roomId ?? formId;
  const collaboratorName = config.collaboratorName ?? 'You';
  const [hasHydratedStorage, setHasHydratedStorage] = useState(false);

  const {
    others,
    updateMyPresence,
    broadcastEvent,
    onEvent,
    storage,
    persistStorage,
  } = useLiveblocksLite(roomId, collaboratorName);

  useEffect(() => {
    if (!storage || hasHydratedStorage) {
      return;
    }

    Object.entries(storage.values).forEach(([fieldName, value]) => {
      form.setValue(fieldName, value, {
        shouldDirty: false,
        shouldValidate: false,
      });
    });
    setHasHydratedStorage(true);
  }, [form, hasHydratedStorage, storage]);

  useEffect(() => onEvent((event) => {
    if (event.type !== 'submit') {
      return;
    }

    const payload = event.payload as { formId?: string; collaborator?: string } | undefined;
    if (!payload || payload.formId !== formId) {
      return;
    }

    toast({
      title: `${payload.collaborator ?? 'A collaborator'} saved this record`,
      variant: 'success',
    });
  }), [formId, onEvent, toast]);

  const lockByFieldName = useMemo(() => {
    const map = new Map<string, string>();

    others.forEach(other => {
      if (other.activeField?.formId !== formId) {
        return;
      }
      map.set(other.activeField.fieldName, other.userName);
    });

    return map;
  }, [formId, others]);

  const handleSubmit = form.handleSubmit(async (values: FieldValues) => {
    const safeValues = values as Record<string, unknown>;
    persistStorage(safeValues);
    await config.onSubmit?.(safeValues);
    broadcastEvent({
      kind: 'form-submitted',
      formId,
      collaborator: collaboratorName,
    });
    updateMyPresence(null);
    onSuccess?.(safeValues);
  });

  return (
    <Form onSubmit={handleSubmit} title={config.title} description={config.description}>
      {config.fields.map((field) => {
          const value = form.watch(field.name);
          const lockedBy = lockByFieldName.get(field.name);
          const isLocked = Boolean(lockedBy);

          const control = renderFieldControl(field, value, (nextValue) => {
            form.setValue(field.name, nextValue, {
              shouldDirty: true,
              shouldValidate: true,
            });
            const latest = form.getValues() as Record<string, unknown>;
            persistStorage({
              ...latest,
              [field.name]: nextValue,
            });
          }, isLocked, () => {
            updateMyPresence({ formId, fieldName: field.name });
          }, () => {
            updateMyPresence(null);
          });

          return (
            <FormItem key={field.name}>
              {lockedBy && (
                <Badge className="w-fit bg-primary text-primary-foreground" variant="secondary">
                  {lockedBy} is editing
                </Badge>
              )}
              <FormLabel>
                <DsFormField
                  id={field.name}
                  label={field.label}
                  required={field.required}
                  description={field.description}
                >
                  <FormControl>
                    {isLocked ? (
                      <div title={`${lockedBy} is currently editing this field.`}>{control}</div>
                    ) : control}
                  </FormControl>
                </DsFormField>
              </FormLabel>
            </FormItem>
          );
        })}

      <div className="flex items-center justify-end gap-3 pt-2">
        <Button type="submit">{config.submitLabel ?? 'Submit'}</Button>
      </div>
      <Typography variant="small" color="muted">
        Powered by react-hook-form field state.
      </Typography>
    </Form>
  );
}
